import { useMemo, useState } from 'react';
import { ChevronUp, ChevronDown, ChevronsUpDown, Search, Upload, Plus } from 'lucide-react';

/**
 * Dense, sortable Leads table — Linear/Stripe-style.
 * Pure presentation: receives data, filters, and callbacks; manages local sort state only.
 *
 * Stage pill color mapping is intentionally restrained:
 *   - "New" / early → neutral
 *   - mid pipeline  → info (blue)
 *   - completed     → warning (amber) until production
 *   - "Sent to Production" → success (green)
 */
const STAGE_TONE = {
  'Paid - Discovery Pending': 'paid',
  'New Lead':              'neutral',
  'Contacted':             'neutral',
  'Discovery Scheduled':   'info',
  'Discovery Completed':   'info',
  'Follow-Up Sent':        'info',
  'Selections Received':   'warning',
  'Interview Scheduled':   'warning',
  'Interview Completed':   'warning',
  'Sent to Production':    'success',
};

const TONE_CLASSES = {
  neutral: 'bg-slate-100 text-slate-600 border-slate-200',
  info:    'bg-blue-50 text-blue-700 border-blue-100',
  warning: 'bg-amber-50 text-amber-700 border-amber-100',
  success: 'bg-emerald-50 text-emerald-700 border-emerald-100',
  paid:    'bg-emerald-100 text-emerald-800 border-emerald-200 font-medium',
};

function StagePill({ stage }) {
  const tone = STAGE_TONE[stage] || 'neutral';
  return (
    <span className={`ops-pill ${TONE_CLASSES[tone]}`} data-testid={`lead-stage-pill-${stage?.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}>
      {stage}
    </span>
  );
}

function SortIcon({ dir }) {
  if (dir === 'asc') return <ChevronUp size={11} className="text-slate-700" />;
  if (dir === 'desc') return <ChevronDown size={11} className="text-slate-700" />;
  return <ChevronsUpDown size={11} className="text-slate-400" />;
}

function fmtDate(s) {
  if (!s) return '—';
  const d = new Date(s);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
}
function fmtRel(s) {
  if (!s) return '—';
  const d = new Date(s);
  if (Number.isNaN(d.getTime())) return '—';
  const diff = Date.now() - d.getTime();
  const day = 86400000;
  if (diff < day) return 'Today';
  if (diff < day * 2) return 'Yesterday';
  if (diff < day * 7) return `${Math.floor(diff / day)}d ago`;
  if (diff < day * 30) return `${Math.floor(diff / (day * 7))}w ago`;
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

export default function LeadsTable({
  leads,
  stages,
  interviewers,
  searchTerm, setSearchTerm,
  stageFilter, setStageFilter,
  ownerFilter, setOwnerFilter,
  onRowClick,
  onAddLead,
  onImportCsv,
  getUserName,
  hideOwnerFilter = false,
}) {
  const [sortKey, setSortKey] = useState('updated_at');
  const [sortDir, setSortDir] = useState('desc');

  const sorted = useMemo(() => {
    const arr = [...leads];
    const getter = {
      client:        l => `${l.client_first_name || ''} ${l.client_last_name || ''}`.toLowerCase(),
      loved_one:     l => (l.loved_one_first_name || '').toLowerCase(),
      stage:         l => l.current_stage || '',
      owner:         l => (getUserName?.(l.owner) || '').toLowerCase(),
      updated_at:    l => l.updated_at || '',
      initial_contact_date: l => l.initial_contact_date || '',
    }[sortKey] || (l => l.updated_at || '');
    arr.sort((a, b) => {
      const va = getter(a); const vb = getter(b);
      if (va < vb) return sortDir === 'asc' ? -1 : 1;
      if (va > vb) return sortDir === 'asc' ? 1 : -1;
      return 0;
    });
    return arr;
  }, [leads, sortKey, sortDir, getUserName]);

  const toggleSort = (k) => {
    if (sortKey === k) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortKey(k); setSortDir('asc'); }
  };

  return (
    <div className="px-6 py-5">
      {/* Filter + actions bar */}
      <div className="flex flex-col lg:flex-row lg:items-center gap-3 mb-4">
        <div className="flex items-center gap-2 flex-1 min-w-0">
          <div className="relative flex-1 max-w-xs">
            <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
              placeholder="Search by client, loved one, email…"
              data-testid="search-leads"
              className="w-full h-8 bg-white border border-slate-200 focus:border-slate-900 focus:ring-1 focus:ring-slate-900 rounded-md pl-8 pr-2.5 text-[13px] text-slate-900 outline-none placeholder:text-slate-400 transition-colors"
            />
          </div>
          <select
            value={stageFilter}
            onChange={e => setStageFilter(e.target.value)}
            data-testid="filter-stage"
            className="h-8 bg-white border border-slate-200 focus:border-slate-900 focus:ring-1 focus:ring-slate-900 rounded-md px-2 text-[13px] text-slate-700 outline-none transition-colors"
          >
            <option value="">All stages</option>
            {stages.map(s => <option key={s} value={s}>{s}</option>)}
          </select>
          {!hideOwnerFilter && (
            <select
              value={ownerFilter}
              onChange={e => setOwnerFilter(e.target.value)}
              data-testid="filter-owner"
              className="h-8 bg-white border border-slate-200 focus:border-slate-900 focus:ring-1 focus:ring-slate-900 rounded-md px-2 text-[13px] text-slate-700 outline-none transition-colors"
            >
              <option value="">All owners</option>
              <option value="unassigned">Unassigned</option>
              {interviewers.filter(u => u.active !== false).map(u => (
                <option key={u._id} value={u._id}>{u.name}</option>
              ))}
            </select>
          )}
          <span className="text-[12px] text-slate-500 ml-1" data-testid="leads-count">{sorted.length} result{sorted.length === 1 ? '' : 's'}</span>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <button
            onClick={onImportCsv}
            data-testid="csv-import-button"
            className="h-8 flex items-center gap-1.5 bg-white hover:bg-slate-50 border border-slate-200 hover:border-slate-300 text-slate-700 px-3 rounded-md text-[13px] font-medium transition-colors"
          >
            <Upload size={13} /> Import CSV
          </button>
          <button
            onClick={onAddLead}
            data-testid="add-lead-button"
            className="h-8 flex items-center gap-1.5 bg-slate-900 hover:bg-slate-800 text-white px-3 rounded-md text-[13px] font-medium shadow-sm transition-colors"
          >
            <Plus size={13} /> Add lead
          </button>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white border border-slate-200 rounded-lg overflow-hidden shadow-sm">
        {sorted.length === 0 ? (
          <div className="text-center py-16 px-6">
            <p className="text-[14px] text-slate-700 font-medium mb-1">No leads match the current filters</p>
            <p className="text-[12px] text-slate-500">Try clearing filters or add a lead manually.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse" data-testid="leads-table">
              <thead className="bg-slate-50/70 border-b border-slate-200">
                <tr className="text-[11px] font-medium text-slate-500 uppercase tracking-wider">
                  {[
                    { key: 'client',     label: 'Client',         w: 'w-[22%]' },
                    { key: 'loved_one',  label: 'Loved one',      w: 'w-[16%]' },
                    { key: 'stage',      label: 'Stage',          w: 'w-[16%]' },
                    { key: 'owner',      label: 'Owner',          w: 'w-[16%]' },
                    { key: 'updated_at', label: 'Last activity',  w: 'w-[15%]' },
                    { key: 'initial_contact_date', label: 'Contact date', w: 'w-[15%]' },
                  ].map(col => (
                    <th
                      key={col.key}
                      data-sortable="true"
                      data-testid={`leads-sort-${col.key}`}
                      onClick={() => toggleSort(col.key)}
                      className={`${col.w} px-4 py-2.5`}
                    >
                      <span className="inline-flex items-center gap-1 hover:text-slate-900">
                        {col.label}
                        <SortIcon dir={sortKey === col.key ? sortDir : null} />
                      </span>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="text-[13px] text-slate-700">
                {sorted.map(lead => (
                  <tr
                    key={lead.id}
                    onClick={() => onRowClick(lead.id)}
                    data-testid={`lead-row-${lead.id}`}
                    className="border-b border-slate-100 last:border-b-0 hover:bg-slate-50 cursor-pointer transition-colors"
                  >
                    <td className="px-4 py-2.5">
                      <div className="flex items-center gap-2">
                        <div className="font-medium text-slate-900 truncate">{lead.client_first_name} {lead.client_last_name}</div>
                        {lead.payment_status === 'paid' && (
                          <span className="ops-pill bg-emerald-100 text-emerald-800 border-emerald-200 px-1.5 text-[10px] shrink-0" data-testid={`paid-badge-${lead.id}`} title={lead.package_name ? `Paid for ${lead.package_name}` : 'Paid lead'}>
                            Paid
                          </span>
                        )}
                      </div>
                      <div className="text-[12px] text-slate-500 truncate">{lead.client_email || '—'}</div>
                    </td>
                    <td className="px-4 py-2.5 text-slate-700 truncate">{lead.loved_one_first_name || '—'}</td>
                    <td className="px-4 py-2.5"><StagePill stage={lead.current_stage} /></td>
                    <td className="px-4 py-2.5 text-slate-700 truncate">{getUserName?.(lead.owner) || 'Unassigned'}</td>
                    <td className="px-4 py-2.5 text-slate-500 whitespace-nowrap">{fmtRel(lead.updated_at)}</td>
                    <td className="px-4 py-2.5 text-slate-500 whitespace-nowrap">{fmtDate(lead.initial_contact_date)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
